import React, { useEffect, useState } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import type { PatternActionSelectionSetModel } from '../../domain/incidents/patternActionSelectionSet';
import {
  PATTERN_ACTION_SELECTION_SET_TITLE,
  PATTERN_ACTION_SELECTION_SET_TOGGLE_COLLAPSE,
  PATTERN_ACTION_SELECTION_SET_TOGGLE_EXPAND,
} from '../../domain/incidents/patternActionSelectionSet';

export type PatternActionSelectionSetProps = {
  model: PatternActionSelectionSetModel;
};

export const PatternActionSelectionSet: React.FC<PatternActionSelectionSetProps> = ({ model }) => {
  const canCollapse = Boolean(model.compactSummary);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setExpanded(!canCollapse);
  }, [canCollapse]);

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      data-testid="pattern-action-selection-set"
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.35 }}>
        {PATTERN_ACTION_SELECTION_SET_TITLE}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.55 }}>
        {model.exactIntro}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.55 }}>
        {model.scopeIntro}
      </Typography>
      {model.compactSummary ? (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, mb: 0.75, lineHeight: 1.55 }}
          data-testid="pattern-action-selection-set-summary"
        >
          {model.compactSummary}
        </Typography>
      ) : null}
      {canCollapse ? (
        <Button
          size="small"
          variant="text"
          sx={{ px: 0, mb: expanded ? 1 : 0, fontWeight: 700 }}
          data-testid="pattern-action-selection-set-toggle"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? PATTERN_ACTION_SELECTION_SET_TOGGLE_COLLAPSE : PATTERN_ACTION_SELECTION_SET_TOGGLE_EXPAND}
        </Button>
      ) : null}
      <Collapse in={expanded || !canCollapse}>
        <Box
          component="dl"
          sx={{
            m: 0,
            mt: canCollapse ? 0 : 0.25,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' },
            gap: 0.75,
            columnGap: 1.5,
            '& dt': { m: 0, fontWeight: 700, fontSize: 13, color: 'text.secondary' },
            '& dd': { m: 0, fontSize: 13, wordBreak: 'break-word' },
          }}
          data-testid="pattern-action-selection-set-details"
        >
          {model.fields.map((field) => (
            <React.Fragment key={field.label}>
              <Typography component="dt" variant="body2">
                {field.label}:
              </Typography>
              <Typography component="dd" variant="body2">
                {field.value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PatternActionSelectionSet;
